import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import LoadingSpinner from "../components/LoadingSpinner";

// Utility to map our formData to API format
const partnerCode = localStorage.getItem("partnerCode");

function prepareApiPayload(formData, createdBy = partnerCode) {
  return {
    id: formData.id || 0,
    userName: formData.username,
    email: formData.email,
    phoneNumber: formData.phoneNumber,
    role: formData.role,
    permissions: formData.permissions.join(', '),
    password: formData.password,
    status: formData.status,
    country: formData.country,
    createdBy,
    updatedBy: createdBy
  };
}

const InventoryCustomer = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phoneNumber: '',
    role: '',
    permissions: [],
    password: '',
    status: '',
    country: '',
    address: ''
  });

  const [error, setError] = useState('');
  const [serverData, setServerData] = useState([]);
  const [viewMode, setViewMode] = useState('customer');
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetching all sub-users on "showData" mode
  useEffect(() => {
    if (viewMode === "showData") {
      fetchSubAccounts();
    }
  }, [viewMode]);

  const fetchSubAccounts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}Renew/GetPartnerSubAccounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*',
        },
        body: JSON.stringify({
          id: 0,
          partnerCode: partnerCode,
          pageSize: 100,
          pageIndex: 1,
        })
      });

      const data = await response.json();
      if (data.isSuccess && data.records) {
        // Filter records to include only those with the role 'Sub_Reseller'
        setServerData(data.records.filter(record => record.role === 'Sub_Reseller'));
      } else {
        toast.error(data.message || 'Failed to fetch records');
      }
    } catch (err) {
      toast.error('Server error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData({
        ...formData,
        permissions: checked
          ? [...formData.permissions, value]
          : formData.permissions.filter((permission) => permission !== value),
      });
    } else if (type === 'select-multiple') {
      const selectedPermissions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData({ ...formData, permissions: selectedPermissions });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.username || !formData.email || !formData.phoneNumber || !formData.role || !formData.status) {
      setError('All required fields must be filled out');
      return;
    }

    const payload = prepareApiPayload(formData, partnerCode);
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}Renew/InsertUpdatePartnerSubAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.isSuccess) {
        toast.success('User data saved to server successfully');
        setFormData({
          username: '',
          email: '',
          phoneNumber: '',
          role: '',
          password: '',
          permissions: [],
          status: '',
          country: '',
          address: ''
        });
        setViewMode('showData');
      } else {
        toast.error(data.message || 'Failed to save user');
      }
    } catch (error) {
      toast.error('Server error saving data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-4 shadow-xl border-2 rounded-lg">
      <h1 className="text-4xl font-bold text-center mb-6">Sub-User Registration</h1>

      {error && <div className="text-red-500 mb-4">{error}</div>}

      {viewMode === "customer" && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">User Name *</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full mt-2 p-3 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full mt-2 p-3 border border-gray-300 rounded-lg"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                className="w-full mt-2 p-3 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">Password *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full mt-2 p-3 border border-gray-300 rounded-lg"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full mt-2 p-3 border border-gray-300 rounded-lg"
                required
              >
                <option value="">Select Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">Country/Region</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full mt-2 p-3 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="flex gap-4 justify-center">
            <button
              type="submit"
              className="bg-teal-500 rounded-full py-3 px-6 text-white shadow-md hover:bg-teal-600 transition duration-200"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save User Data'}
            </button>
            <button
              type="button"
              onClick={() => setViewMode("showData")}
              className="bg-[#117575] rounded-full py-3 px-6 text-white shadow-md hover:bg-[#1cbebe] transition duration-200"
              disabled={loading}
            >
              Show Saved Data
            </button>
          </div>
        </form>
      )}

      {/* Show Data View */}
      {viewMode === "showData" && (
        <div className="overflow-x-auto mt-6">
          {loading ? (
            <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
              <LoadingSpinner />
            </div>
          ) : (
            <table className="w-full rounded-lg shadow-lg bg-white">
              <thead className="sticky top-0 bg-gray-200">
                <tr className="border-b">
                  <th className="text-center p-3">S.NO</th>
                  <th className="text-center p-3">Username</th>
                  <th className="text-center p-3">Email</th>
                  <th className="text-center p-3">Phone Number</th>
                  <th className="text-center p-3">Role</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-center p-3">Country/Region</th>
                  <th className="text-center p-3">Password</th>
                </tr>
              </thead>
              {serverData.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan="8" className="text-center py-10 text-gray-500">
                      No Active Sub-user accounts found.
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody>
                  {serverData.map((row, index) => (
                    <tr key={row.id} className="border-b hover:bg-gray-100">
                      <td className="text-center p-2">{index + 1}</td>
                      <td className="text-center p-2">{row.userName}</td>
                      <td className="text-center p-2">{row.email}</td>
                      <td className="text-center p-2">{row.phoneNumber}</td>
                      <td className="text-center p-2">{row.role}</td>
                      <td className="text-center p-2">{row.status}</td>
                      <td className="text-center p-2">{row.country}</td>
                      <td className="text-center p-2">{row.password}</td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          )}
          <div className="flex justify-center mt-4">
            <button
              type="button"
              onClick={() => setViewMode("customer")}
              className="rounded-lg bg-[#117575] px-6 py-3 text-white font-bold shadow-lg hover:bg-[#1cbebe] transition duration-200"
            >
              Back to Form
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryCustomer;
